import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { packlistKey, PACKLISTS_RESOURCE } from '../packlists/types';
import { RowMenu, type RowAction } from '../ui/RowMenu';
import { PacklistCompareDialog } from './PacklistCompareDialog';
import { DoneNotice, DraftCreatedError, FillChoiceDialog, fillDraftFromPacklist, fillText, newVersionFromPacklist, type FillResult, type NoDraft } from './FillFromPacklist';
import { LINES_RESOURCE, QUOTES_KEY, QUOTES_RESOURCE, quoteKey, type QuoteOfEvent, type Version, type VersionSummary } from './types';
import { Notice } from '../ui/Notice';

/** Answers of F25 (quote ↔ packlist). */
export type { FillResult };
/** `packlistVersion` / `newVersion` (owner 2026-10-22 14:29): a packlist with anything got a new version. */
export type ToPacklistResult = { added: number; changed: number; removed: number; unchanged: number; skipped: Array<{ name: string; reason: string }>; packlistVersion?: number; newVersion?: boolean; alreadyInSync?: boolean };

/** "Sprzętówka: dodano 2, zmieniono 1, pominięto 3 (usługi i pozycje własne)." */
export function toPacklistText(r: ToPacklistResult): string {
  const parts = [`dodano ${r.added}`, `zmieniono ${r.changed}`];
  if (r.removed > 0) parts.push(`usunięto ${r.removed}`);
  if (r.unchanged > 0) parts.push(`bez zmian ${r.unchanged}`);
  if (r.skipped.length > 0) parts.push(`pominięto ${r.skipped.length} (usługi i pozycje własne)`);
  if (r.alreadyInSync && r.packlistVersion) return `Sprzętówka jest już zgodna z kosztorysem (v${r.packlistVersion}).`;
  if (r.newVersion && r.packlistVersion) return `Utworzono wersję v${r.packlistVersion} sprzętówki (poprzednia zostaje w wersjach): ${parts.join(', ')}.`;
  return `Sprzętówka z kosztorysu: ${parts.join(', ')}.`;
}

/** The message of a failed fill for the choice dialog (a draft made meanwhile says so; no "no_draft": the dialog changes instead). */
const fillError = (error: unknown): string | undefined =>
  error instanceof DraftCreatedError ? error.message : error instanceof ApiError ? (noDraftOf(error) ? undefined : error.message) : error ? 'Nie udało się uzupełnić kosztorysu' : undefined;

const noDraftOf = (error: unknown): NoDraft | null => (error instanceof ApiError && error.status === 409 && error.code === 'no_draft' ? ((error.current as NoDraft | undefined) ?? null) : null);

/** What the user may do between the two (the server checks the same). */
export function packlistRights(me: Me) {
  const quantities = fieldVisible(me, PACKLISTS_RESOURCE, 'quantity') && fieldVisible(me, LINES_RESOURCE, 'quantity');
  return {
    view: can(me, PACKLISTS_RESOURCE, 'view') && quantities,
    edit: can(me, PACKLISTS_RESOURCE, 'edit') && quantities,
  };
}

/**
 * The quote's side (menu „…” of the quote): "Uzupełnij ze sprzętówki" (the newest version; a sent one only gets a
 * notice and "Nowa wersja ze sprzętówki"), "Utwórz sprzętówkę z kosztorysu" (any version), "Porównaj ze sprzętówką".
 * Returns the menu entries and what to render (the message after an action, the comparison).
 */
export function useQuotePacklistActions({
  me,
  eventId,
  quoteId,
  version,
  versions,
  phone,
  differs = false,
  onNewDraft,
}: {
  me: Me | undefined;
  eventId: string;
  quoteId: string | null;
  version: Version | null;
  versions: VersionSummary[];
  phone: boolean;
  /** F27: the banner "Sprzętówka różni się od kosztorysu" (the current version is shown and differs). */
  differs?: boolean;
  onNewDraft: () => void;
}): {
  menu: RowAction[];
  view: ReactNode;
} {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState('');
  const [comparing, setComparing] = useState(false);
  /** Owner 2026-10-22 01:25: "Uzupełnij" asks first — overwrite the draft or a new version (of a sent one). */
  const [choosing, setChoosing] = useState<VersionSummary | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
    void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
  };
  const fill = useMutation({
    mutationFn: (target: { draftId: string } | NoDraft) => ('draftId' in target ? fillDraftFromPacklist(target.draftId) : newVersionFromPacklist(target)),
    onSuccess: (r) => {
      setChoosing(null);
      // „Anuluj” in „Dodaj mimo to” (owner 2026-10-24): nothing filled (a new draft may stay empty)
      if (!r) return onNewDraft();
      setNotice(fillText(r));
      // the filled draft (maybe new) is the current version
      onNewDraft();
    },
    onError: (error) => {
      // re-review M3: someone made a draft meanwhile — the choice again, with that draft (never overwritten unasked)
      if (error instanceof DraftCreatedError) return setChoosing(error.draft);
      // sent in the meantime: the choice again, now with only a new version
      const n = noDraftOf(error);
      const v = n && versions.find((x) => x.id === n.versionId);
      if (v) setChoosing({ ...v, state: v.state === 'robocza' ? 'wyslana' : v.state });
    },
    onSettled: refresh,
  });
  const generate = useMutation({
    mutationFn: (v: Version) => withConflictConfirm((confirmConflicts) => api<ToPacklistResult>(`/api/quotes/versions/${v.id}/to-packlist`, { method: 'POST', body: confirmConflicts ? { confirmConflicts } : {} })),
    onSuccess: (r) => {
      if (r) setNotice(toPacklistText(r));
    },
    onSettled: refresh,
  });
  if (!me || !quoteId || !version) return { menu: [], view: null };
  const rights = packlistRights(me);
  const mayEdit = can(me, QUOTES_RESOURCE, 'edit') && !phone && rights.view;
  /**
   * "Uzupełnij ze sprzętówki" works on the newest version: it asks first (owner 2026-10-22 01:25) — a draft is
   * overwritten; a sent / accepted one is never touched, a new version from it is filled ("Nowa wersja ze sprzętówki").
   */
  const fillNewest = () => {
    setNotice('');
    fill.reset();
    setChoosing(versions.find((x) => x.state === 'robocza') ?? versions[versions.length - 1]!);
  };
  const menu: RowAction[] = [];
  if (mayEdit) menu.push({ label: 'Uzupełnij ze sprzętówki', onSelect: fillNewest });
  if (rights.edit) menu.push({ label: 'Utwórz sprzętówkę z kosztorysu', onSelect: () => generate.mutate(version) });
  if (rights.view) menu.push({ label: 'Porównaj ze sprzętówką', onSelect: () => setComparing(true) });
  const fillWays: RowAction[] = [
    ...(mayEdit ? [{ label: 'Kosztorys ze sprzętówki', onSelect: fillNewest, disabled: fill.isPending }] : []),
    ...(rights.edit ? [{ label: 'Sprzętówka z kosztorysu', onSelect: () => generate.mutate(version), disabled: generate.isPending }] : []),
  ];
  // re-review M5: an error of filling shows in the choice dialog while it is open
  const error = noDraftOf(fill.error) || choosing ? generate.error : (fill.error ?? generate.error);
  const view = (
    <>
      {differs && rights.view && !choosing && !comparing && (
        <div className="notice q-packlist-differs" role="status" data-feature-id="quotes.sync">
          Sprzętówka różni się od kosztorysu.{' '}
          <button className="btn small" type="button" onClick={() => setComparing(true)}>
            Porównaj
          </button>{' '}
          {/* owner 2026-10-23 20:34: "Uzupełnij" offers both ways — the quote from the packlist, the packlist from the quote */}
          <RowMenu label="Uzupełnij" text="Uzupełnij ▾" actions={fillWays} />
        </div>
      )}
      {notice && !error && <DoneNotice text={notice} onClose={() => setNotice('')} featureId="quotes.from_packlist" />}
      {choosing && (
        <FillChoiceDialog
          newest={choosing}
          busy={fill.isPending}
          error={fillError(fill.error)}
          onOverwrite={() => fill.mutate({ draftId: choosing.id })}
          onNewVersion={() => fill.mutate({ quoteId, versionId: choosing.id })}
          onClose={() => setChoosing(null)}
        />
      )}
      {error && (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Nie udało się'}
        </p>
      )}
      {comparing && (
        <PacklistCompareDialog
          version={version}
          mayFill={mayEdit}
          mayGenerate={rights.edit}
          busy={fill.isPending || generate.isPending}
          onGenerate={() => {
            setComparing(false);
            generate.mutate(version);
          }}
          onFill={() => {
            setComparing(false);
            fillNewest();
          }}
          onClose={() => setComparing(false)}
        />
      )}
    </>
  );
  return { menu, view };
}

/**
 * What "Przejdź do kosztorysu" did about transport (the server's answer, owner 2026-10-21 23:31). `skipped`: none, for a
 * role that does not see the event's place (not told whether it has an address); `failed`: refused after the fill.
 */
export type TransportOutcome = 'added' | 'exists' | 'no_address' | 'no_service' | 'skipped' | 'failed';
export type GoToQuoteResult = FillResult & { transport?: TransportOutcome; transportError?: string };

const TRANSPORT_TEXT: Record<Exclude<TransportOutcome, 'failed'>, string> = {
  added: 'Dodano transport wg adresu wydarzenia.',
  exists: '',
  no_address: 'Bez transportu: wydarzenie nie ma adresu — dodaj go w szczegółach, potem „+ Transport”.',
  no_service: 'Bez transportu: w cenniku nie ma „Transport ciężarowy”.',
  skipped: 'Bez transportu — w razie potrzeby dodaj go w kosztorysie („+ Transport”).',
};

/** "Utworzono kosztorys: dodano 3, … Dodano transport wg adresu wydarzenia." (a refused truck: "Transportu nie dodano: …"). */
export function goToQuoteText(r: GoToQuoteResult): string {
  const transport = !r.transport ? '' : r.transport === 'failed' ? `Transportu nie dodano: ${(r.transportError ?? 'błąd').replace(/\.$/, '')}.` : TRANSPORT_TEXT[r.transport];
  return [fillText(r), transport].filter(Boolean).join(' ');
}

/**
 * The notice the Kosztorys tab shows after "Przejdź do kosztorysu". What was done closes itself like after "Uzupełnij"
 * (owner 01:25: "wyświetl info na zielono i zamknij to, nie trzymaj na wierzchu"); `lasting`: a line that must stay
 * read — the sent quote left as it is, the truck refused.
 */
export type GoToQuoteNotice = { text: string; lasting: boolean };

/** The quote's newest version is sent: nothing changed in it (no "are you sure", owner 22:23). */
export const FROZEN_QUOTE_TEXT = 'Kosztorys jest już wysłany do klienta — nic w nim nie zmieniono. Nową wersję ze sprzętówki zaproponuje „…” → „Uzupełnij ze sprzętówki”.';

/** `GoToQuoteNotice` over the quote: the self-closing green `DoneNotice`, or a static line when `lasting`. */
export function GoToQuoteNoticeView({ notice, onClose }: { notice: GoToQuoteNotice; onClose: () => void }) {
  if (!notice.lasting) return <DoneNotice text={notice.text} onClose={onClose} featureId="quotes.from_packlist" />;
  return (
    <Notice data-feature-id="quotes.from_packlist" onClose={onClose}>
      {notice.text}
    </Notice>
  );
}

/** The fill would lower or remove lines or change hand-typed quantities: the server asks first (coordinator 2026-10-22). */
const needsChoiceOf = (error: unknown): VersionSummary | null => {
  if (!(error instanceof ApiError && error.status === 409 && error.code === 'fill_needs_choice')) return null;
  const c = error.current as { versionId: string; number: number } | undefined;
  return c ? { id: c.versionId, number: c.number, state: 'robocza', outdated: false, sentAt: null } : null;
};

/**
 * "Przejdź do kosztorysu" in the packlist (owner 2026-10-21 23:31: "guzik przejścia do kosztorysu już uzupełnionego o tą
 * sprzętówkę i o transport na podstawie adresu"). One click: no quote → one is made from the packlist; a quote with a
 * draft → the draft is filled silently when it only gains lines or pieces (the wizard's untouched draft); when the
 * fill would overwrite quantities typed by hand or remove lines, the choice of "Uzupełnij" comes first (owner 01:25
 * "zapytaj czy dodać nową wersję czy nadpisać"; coordinator 2026-10-22, docs/decyzje-etap-3.md). There is never a
 * second quote; transport by the address only when the draft has none. A sent quote is not touched (the Kosztorys tab
 * says so). Then `onDone(notice)` opens the tab. Like editing a quote: not on a phone; not for a quote someone else
 * holds (P16). `view`: the choice dialog, to render.
 */
export function useGoToQuote(me: Me | undefined, eventId: string, phone: boolean, onDone: ((notice: GoToQuoteNotice) => void) | undefined) {
  const queryClient = useQueryClient();
  const allowed = !!onDone && !phone && !!me && packlistRights(me).view && can(me, QUOTES_RESOURCE, 'view') && can(me, QUOTES_RESOURCE, 'edit');
  const quote = useQuery({ queryKey: quoteKey(eventId), enabled: allowed, retry: false, queryFn: () => api<QuoteOfEvent>(`/api/quotes/events/${eventId}`) });
  const [choosing, setChoosing] = useState<VersionSummary | null>(null);
  const go = useMutation({
    mutationFn: async (overwrite: boolean): Promise<GoToQuoteNotice | null> => {
      try {
        const r = await withConflictConfirm((confirmConflicts) =>
          api<GoToQuoteResult>(`/api/quotes/events/${eventId}/from-packlist`, { method: 'POST', body: { transport: true, ...(overwrite ? { overwrite: true } : {}), ...(confirmConflicts ? { confirmConflicts } : {}) } }),
        );
        if (!r) return null;
        return { text: goToQuoteText(r), lasting: r.transport === 'failed' };
      } catch (error) {
        if (noDraftOf(error)) return { text: FROZEN_QUOTE_TEXT, lasting: true };
        const draft = needsChoiceOf(error);
        if (draft) {
          setChoosing(draft);
          return null;
        }
        throw error;
      }
    },
    onSuccess: async (notice) => {
      if (!notice) return;
      setChoosing(null);
      await queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      onDone?.(notice);
    },
  });
  const show = allowed && !!quote.data && !quote.data.heldByOther && (!!quote.data.quote || can(me!, QUOTES_RESOURCE, 'create'));
  const message = go.error ? (go.error instanceof ApiError ? go.error.message : 'Nie udało się przejść do kosztorysu') : null;
  const view = choosing && (
    <FillChoiceDialog
      newest={choosing}
      busy={go.isPending}
      error={message ?? undefined}
      onOverwrite={() => go.mutate(true)}
      onNewVersion={() => undefined}
      onClose={() => {
        setChoosing(null);
        go.reset();
      }}
    />
  );
  return { show, busy: go.isPending, error: choosing ? null : message, run: () => go.mutate(false), view };
}
